import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button, FormControlLabel, IconButton, Link, Skeleton, Switch, Tooltip, Typography } from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PageContainer from '../../../components/PageContainer';
import { AppDialog, EmptyState, PageHeader, Panel, StatusChip } from '../../../components/ui';
import { CustomerFormSubmissions } from '../../../store/slices';
import { IStore } from '../../../store/types';
import { getAllCustomerFormSubmissions, normalizeFormSubmissionTypes, WHOLESALE_REQUEST_TYPE_ID } from '../../../api/customerFormSubmissions';
import { formatDate, InfoRow, SectionCard } from './shared';
import WholesaleRequestView from './WholesaleRequestView';
import GenericFormDataView from './GenericFormDataView';

const LIST_PATH = '/customer-form-submissions';

/** One form submission: the overview, the form data (wholesale layout or generic), processed toggle and delete. */
const OneCustomerFormSubmission = () => {
    const { submissionId } = useParams<{ submissionId: string }>();
    const dispatch = useDispatch();
    const navigator = useNavigate();
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [siblingIds, setSiblingIds] = useState<number[]>([]);
    const [siblingsTypeId, setSiblingsTypeId] = useState<number | null>(null);

    const { singleSubmission, singleStatus, types } = useSelector<IStore, any>(
        (state) => state.customerFormSubmissions || { singleSubmission: null, singleStatus: null, types: [] }
    );

    useEffect(() => {
        if (submissionId) {
            dispatch<any>(CustomerFormSubmissions.getCustomerFormSubmission(submissionId));
        }
        dispatch<any>(CustomerFormSubmissions.getFormSubmissionTypes());
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when the id in the URL changes
    }, [submissionId]);

    const submission = singleSubmission;
    const typesList = normalizeFormSubmissionTypes(types);

    // Same-type ids in table order (newest first) so the arrows can step between submissions without going back to the list.
    useEffect(() => {
        const typeId = submission?.typeId;
        if (typeId == null || typeId === siblingsTypeId) return;
        let cancelled = false;
        (async () => {
            try {
                const data = await getAllCustomerFormSubmissions({ page: 1, limit: 1000, sortDirection: 'DESC', typeId });
                if (cancelled) return;
                setSiblingIds((data?.result || []).map((item: any) => +item.id));
                setSiblingsTypeId(typeId);
            } catch (e) {
                if (!cancelled) {
                    setSiblingIds([]);
                    setSiblingsTypeId(null);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [submission?.typeId, siblingsTypeId]);

    const currentIndex = siblingIds.indexOf(Number(submissionId));
    const prevId = currentIndex > 0 ? siblingIds[currentIndex - 1] : null;
    const nextId = currentIndex >= 0 && currentIndex < siblingIds.length - 1 ? siblingIds[currentIndex + 1] : null;

    const goToSibling = (id: number | null) => {
        if (id != null) navigator(`${LIST_PATH}/${id}`);
    };

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (deleteOpen || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
            const tag = (e.target as HTMLElement)?.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if (e.key === 'ArrowLeft') goToSibling(prevId);
            if (e.key === 'ArrowRight') goToSibling(nextId);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- goToSibling only depends on the ids
    }, [prevId, nextId, deleteOpen]);

    const typeLabel = typesList.find((t: any) => t.id === submission?.typeId)?.label
        || submission?.typeLabel
        || (submission?.typeId ? `Type #${submission.typeId}` : '—');

    const handleProcessedToggle = async () => {
        if (!submissionId || !submission || updating) return;
        setUpdating(true);
        try {
            await dispatch<any>(CustomerFormSubmissions.patchCustomerFormSubmission({ submissionId, processed: !submission.processed }));
        } finally {
            setUpdating(false);
        }
    };

    const handleDelete = async () => {
        if (!submissionId || deleting) return;
        setDeleting(true);
        try {
            await dispatch<any>(CustomerFormSubmissions.removeCustomerFormSubmission(submissionId));
            navigator(LIST_PATH);
        } finally {
            setDeleting(false);
        }
    };

    if (singleStatus === 'loading') {
        return (
            <PageContainer constrained>
                <Skeleton animation="wave" width="100%" height={600} variant="rounded" />
            </PageContainer>
        );
    }

    if (!submission || singleStatus !== 'resolved') {
        return (
            <PageContainer constrained>
                <PageHeader eyebrow="Production" title="Form submission" backTo={{ to: LIST_PATH, label: 'Form submissions' }} />
                <Panel>
                    <EmptyState
                        title={singleStatus === 'rejected' ? 'The submission could not be loaded' : 'Submission not found'}
                        action={<Button variant="outlined" onClick={() => navigator(LIST_PATH)}>Back to the list</Button>}
                    />
                </Panel>
            </PageContainer>
        );
    }

    const formData = submission.formData || submission.data || submission.payload;
    const isWholesaleRequest = submission.typeId === WHOLESALE_REQUEST_TYPE_ID;
    const processed = Boolean(submission.processed);

    return (
        <PageContainer constrained>
            <PageHeader
                eyebrow="Production"
                title={typeLabel}
                backTo={{ to: LIST_PATH, label: 'Form submissions' }}
                badges={processed ? <StatusChip tone="success">Processed</StatusChip> : <StatusChip tone="warning">Unprocessed</StatusChip>}
                meta={(
                    <>
                        <span>Submission #{submission.id}</span>
                        {submission.orderNumber ? <span>Order #{submission.orderNumber}</span> : null}
                        <span>{formatDate(submission.createdAt)}</span>
                        {siblingIds.length > 0 && currentIndex >= 0 && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                                <Tooltip title="Previous submission of this type (←)">
                                    <span>
                                        <IconButton size="small" onClick={() => goToSibling(prevId)} disabled={prevId == null} aria-label="Previous submission">
                                            <NavigateBeforeIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                {currentIndex + 1} / {siblingIds.length}
                                <Tooltip title="Next submission of this type (→)">
                                    <span>
                                        <IconButton size="small" onClick={() => goToSibling(nextId)} disabled={nextId == null} aria-label="Next submission">
                                            <NavigateNextIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </span>
                        )}
                    </>
                )}
                actions={(
                    <>
                        <FormControlLabel
                            sx={{ m: 0 }}
                            control={<Switch checked={processed} onChange={handleProcessedToggle} disabled={updating} color="success" />}
                            label="Processed"
                        />
                        <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={() => setDeleteOpen(true)}>
                            Delete
                        </Button>
                    </>
                )}
            />

            {isWholesaleRequest && formData ? (
                <WholesaleRequestView submission={submission} formData={formData} typeLabel={typeLabel} />
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 16 }}>
                    <SectionCard title="Overview">
                        <InfoRow label="ID" value={submission.id} />
                        <InfoRow label="Email" value={submission.email ? <Link href={`mailto:${submission.email}`}>{submission.email}</Link> : '—'} />
                        <InfoRow label="Form type" value={typeLabel} />
                        {submission.orderNumber && <InfoRow label="Order number" value={`#${submission.orderNumber}`} />}
                        <InfoRow label="Processed" value={processed ? <StatusChip tone="success" size="sm">Yes</StatusChip> : <StatusChip tone="warning" size="sm">No</StatusChip>} />
                        <InfoRow label="Created" value={formatDate(submission.createdAt)} />
                        <InfoRow label="Updated" value={formatDate(submission.updatedAt)} />
                    </SectionCard>
                    {formData ? (
                        <GenericFormDataView formData={formData} />
                    ) : (
                        <SectionCard title="Form data">
                            <Typography color="text.secondary">No form data</Typography>
                        </SectionCard>
                    )}
                </div>
            )}

            <AppDialog
                open={deleteOpen}
                onClose={() => !deleting && setDeleteOpen(false)}
                title={`Delete submission #${submission.id}`}
                description="The submission is removed for everyone. This cannot be undone."
                pending={deleting}
                maxWidth="xs"
                actions={(
                    <>
                        <Button variant="text" onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
                        <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>Delete</Button>
                    </>
                )}
            />
        </PageContainer>
    );
};

export default OneCustomerFormSubmission;
